import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bell, CornerDownRight, Minus, Pencil, Plus, Tag, Trash2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription } from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/AdminLayout";
import { StockBadge, Pill } from "@/components/Badges";
import { ProductFormDialog } from "@/components/ProductFormDialog";
import { BacWaterCard } from "@/components/BacWaterCard";
import { SubcategoriesDialog } from "@/components/SubcategoriesDialog";
import { api, errMsg, money, groupProducts, CATEGORY_LABELS } from "@/lib/api";

export default function Inventory() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [subcats, setSubcats] = useState([]);
  const [subOpen, setSubOpen] = useState(false);
  const [subFilter, setSubFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/admin/products").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/subcategories").then((r) => setSubcats(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const allGroups = useMemo(() => groupProducts(list), [list]);
  useEffect(() => {
    if (subFilter !== "all" && !subcats.some((s) => s.id === subFilter)) setSubFilter("all");
  }, [subcats, subFilter]);
  const groups = useMemo(() => (subFilter === "all" ? allGroups : allGroups.filter((g) => (g.items[0].subcategory_ids || []).includes(subFilter))), [allGroups, subFilter]);
  const groupOf = (p) => allGroups.find((g) => g.id === (p.group_id || p.id))?.items || [p];
  const subName = Object.fromEntries(subcats.map((s) => [s.id, s.name]));
  const subCounts = useMemo(() => {
    const c = {};
    allGroups.forEach((g) => (g.items[0].subcategory_ids || []).forEach((id) => { c[id] = (c[id] || 0) + 1; }));
    return c;
  }, [allGroups]);

  const stock = async (p, delta) => {
    try {
      await api.post(`/admin/products/${p.id}/stock`, { delta });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleSale = async (p, on) => {
    if (on && p.sale_price == null) {
      setEditing(groupOf(p));
      setFormOpen(true);
      toast.info("Set a sale price first");
      return;
    }
    const { id, effective_price, stock_status, created_at, margin_pct, waiting, on_order_boxes, on_order_vials, ...body } = p;
    try {
      await api.put(`/admin/products/${id}`, { ...body, on_sale: on });
      toast.success(on ? `${p.name} is on sale` : "Sale ended");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/products/${deleting.id}`);
      toast.success("Product deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Stock" title="Inventory">
        <Button variant="outline" className="gap-2" onClick={() => setSubOpen(true)} data-testid="inventory-subcategories-button"><Tag className="h-4 w-4" />Sub-categories</Button>
        <Button className="gap-2" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="inventory-add-product-button"><Plus className="h-4 w-4" />Add product</Button>
      </PageHeader>
      <BacWaterCard />
      {subcats.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="inventory-subcategory-filters">
          {[{ id: "all", name: "All" }, ...subcats].map((s) => (
            <button key={s.id} onClick={() => setSubFilter(s.id)} data-testid={`inventory-subfilter-${s.id}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${subFilter === s.id ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"}`}>{s.name}</button>
          ))}
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead><TableHead>Category</TableHead><TableHead>List price</TableHead><TableHead>Your cost / margin</TableHead><TableHead>On sale</TableHead>
              <TableHead>Stock</TableHead><TableHead>Rec. BAC</TableHead><TableHead>Shop</TableHead><TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.flatMap((g) => g.items.map((p, idx) => (
              <TableRow key={p.id} data-testid={`inventory-row-${p.id}`} className={idx > 0 ? "border-t-0" : ""}>
                <TableCell>
                  {idx === 0
                    ? <>
                        <span className="font-medium">{p.name}</span> <span className="font-mono-data text-xs text-muted-foreground">{p.vial_size}</span>
                        {(p.subcategory_ids || []).length > 0 && (
                          <span className="mt-1 flex flex-wrap gap-1" data-testid={`inventory-subcats-${p.id}`}>
                            {p.subcategory_ids.filter((id) => subName[id]).map((id) => <span key={id} className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{subName[id]}</span>)}
                          </span>
                        )}
                      </>
                    : <span className="flex items-center gap-1.5 pl-3 font-mono-data text-xs text-muted-foreground"><CornerDownRight className="h-3 w-3" />{p.vial_size}</span>}
                </TableCell>
                <TableCell className="text-muted-foreground">{CATEGORY_LABELS[p.category]}</TableCell>
                <TableCell className="font-mono-data">
                  {p.on_sale ? <><span className="text-orange-400">{money(p.sale_price)}</span> <span className="text-xs text-muted-foreground line-through">{money(p.price)}</span></> : money(p.price)}
                </TableCell>
                <TableCell className="font-mono-data text-sm" data-testid={`inventory-margin-${p.id}`}>
                  {p.cost > 0 ? <>{money(p.cost)} <span className={p.margin_pct < 20 ? "text-amber-400" : "text-emerald-400"}>· {p.margin_pct}%</span></> : <span className="text-xs text-muted-foreground">no cost set</span>}
                </TableCell>
                <TableCell><Switch checked={p.on_sale} onCheckedChange={(v) => toggleSale(p, v)} data-testid={`inventory-sale-switch-${p.id}`} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => stock(p, -1)} disabled={p.stock <= 0} data-testid={`inventory-stock-minus-${p.id}`}><Minus className="h-3 w-3" /></Button>
                    <span className="w-8 text-center font-mono-data" data-testid={`inventory-stock-value-${p.id}`}>{p.stock}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => stock(p, 1)} data-testid={`inventory-stock-plus-${p.id}`}><Plus className="h-3 w-3" /></Button>
                    <StockBadge status={p.stock_status} />
                    {p.waiting > 0 && <span title="Clients waiting for a back-in-stock email" className="flex items-center gap-1 font-mono-data text-xs text-sky-400" data-testid={`inventory-waiting-${p.id}`}><Bell className="h-3 w-3" />{p.waiting}</span>}
                    {p.on_order_boxes > 0 && <span title="Boxes ordered from vendors, not arrived yet" className="flex items-center gap-1 whitespace-nowrap font-mono-data text-xs text-violet-300" data-testid={`inventory-on-order-${p.id}`}><Truck className="h-3 w-3" />{p.on_order_boxes} box{p.on_order_boxes > 1 ? "es" : ""} coming</span>}
                  </div>
                </TableCell>
                <TableCell className="font-mono-data">{p.category === "peptide" ? `${p.recommended_bac_ml} mL` : "—"}</TableCell>
                <TableCell>{p.active ? <Pill color="emerald">Visible</Pill> : <Pill>Hidden</Pill>}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(g.items); setFormOpen(true); }} data-testid={`inventory-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleting(p)} data-testid={`inventory-delete-${p.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
        {list.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No products yet.</p>}
        {list.length > 0 && groups.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="inventory-filter-empty">No products in this sub-category yet.</p>}
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Tag className="h-3 w-3" />Clients only see In Stock / Low Stock / Out of Stock — never exact counts.</p>
      <ProductFormDialog open={formOpen} group={editing} onClose={() => setFormOpen(false)} onSaved={load} />
      <SubcategoriesDialog open={subOpen} onClose={() => setSubOpen(false)} onChanged={load} counts={subCounts} />
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name} {deleting?.vial_size}?</AlertDialogTitle>
            <AlertDialogDescription>Past orders keep their details. Tip: hide it from the shop instead if you may restock.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
